import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { MessageSquare, Download, RefreshCw, AlertCircle, TrendingUp, Heart, BarChart3, FileJson, FileSpreadsheet, CreditCard, ExternalLink } from 'lucide-react';
import { exportCommentsCSV, exportCommentsJSON, getCreditBalance } from '../../lib/api';
import { useEnhancedComments } from '../../hooks/useEnhancedComments';
import { CommentsPreview } from './CommentsPreview';
import { CreditWarningModal } from './CreditWarningModal';
import { CommentsProgress } from './CommentsProgress';
interface CommentAnalysisProps {
  taskId: string;
  videoId?: string;
}
export function CommentAnalysis({
  taskId,
  videoId
}: CommentAnalysisProps) {
  const {
    preview,
    previewLoading,
    previewError,
    fetching,
    progress,
    results,
    showCreditModal,
    setShowCreditModal,
    alreadyFetched,
    insufficientCredits,
    creditsError,
    initiateFetch
  } = useEnhancedComments(taskId);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [currentCredits, setCurrentCredits] = useState<number | null>(null);
  // Load current credit balance when insufficient credits error occurs
  useEffect(() => {
    if (insufficientCredits) {
      loadCredits();
    }
  }, [insufficientCredits]);
  const loadCredits = async () => {
    try {
      const balance = await getCreditBalance();
      setCurrentCredits(balance);
    } catch (error) {
      console.error('Error loading credits:', error);
    }
  };
  const handleFetchClick = () => {
    setShowCreditModal(true);
  };
  const handleConfirmFetch = () => {
    initiateFetch();
  };
  const handleExportCSV = async () => {
    setIsExporting(true);
    setExportError(null);
    try {
      await exportCommentsCSV(taskId);
    } catch (err) {
      console.error('Error exporting CSV:', err);
      setExportError('Failed to export CSV. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };
  const handleExportJSON = async () => {
    setIsExporting(true);
    setExportError(null);
    try {
      await exportCommentsJSON(taskId);
    } catch (err) {
      console.error('Error exporting JSON:', err);
      setExportError('Failed to export JSON. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };
  // Show insufficient credits error
  if (insufficientCredits && creditsError) {
    return <div className="border border-destructive/30 rounded-lg bg-destructive/5" data-id="element-1051">
        <div className="p-4" data-id="element-1052">
          <div className="flex items-start gap-3 mb-4" data-id="element-1053">
            <AlertCircle size={20} className="text-destructive mt-0.5 flex-shrink-0" data-id="element-1054" />
            <div className="flex-grow" data-id="element-1055">
              <h4 className="font-medium text-sm text-destructive mb-1" data-id="element-1056">
                Insufficient Credits
              </h4>
              <p className="text-sm text-muted-foreground mb-3" data-id="element-1057">
                {creditsError}
              </p>
              {preview && <div className="bg-secondary/30 rounded-lg p-3 mb-3" data-id="element-1058">
                  <div className="grid grid-cols-2 gap-3 text-xs" data-id="element-1059">
                    <div data-id="element-1060">
                      <span className="text-muted-foreground" data-id="element-1061">
                        Estimated comments:
                      </span>
                      <span className="font-medium ml-2" data-id="element-1062">
                        {preview.estimated_total.toLocaleString()}
                      </span>
                    </div>
                    <div data-id="element-1063">
                      <span className="text-muted-foreground" data-id="element-1064">
                        Credits needed:
                      </span>
                      <span className="font-medium ml-2" data-id="element-1065">
                        {preview.credits_needed}
                      </span>
                    </div>
                    {currentCredits !== null && <>
                        <div data-id="element-1066">
                          <span className="text-muted-foreground" data-id="element-1067">
                            Your balance:
                          </span>
                          <span className="font-medium ml-2" data-id="element-1068">
                            {currentCredits} credits
                          </span>
                        </div>
                        <div data-id="element-1069">
                          <span className="text-muted-foreground" data-id="element-1070">Need:</span>
                          <span className="font-medium ml-2 text-destructive" data-id="element-1071">
                            {Math.max(0, preview.credits_needed - currentCredits)}{' '}
                            more
                          </span>
                        </div>
                      </>}
                  </div>
                </div>}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2" data-id="element-1072">
            <Button variant="default" size="sm" className="flex-1" onClick={() => window.open('/app/settings', '_blank')} data-id="element-1073">
              <CreditCard size={14} className="mr-2" data-id="element-1074" />
              Buy Credits
            </Button>
            <Button variant="outline" size="sm" className="flex-1" onClick={() => window.location.reload()} data-id="element-1075">
              <RefreshCw size={14} className="mr-2" data-id="element-1076" />
              Refresh
            </Button>
          </div>

          <div className="mt-3 pt-3 border-t border-border/30" data-id="element-1077">
            <p className="text-xs text-muted-foreground" data-id="element-1078">
              💡 <strong data-id="element-1079">How it works:</strong> 1 credit = 100 comments. Credits
              are only charged after successful analysis.
            </p>
          </div>
        </div>
      </div>;
  }
  // Show progress if fetching
  if (fetching && progress) {
    return <CommentsProgress progress={progress} data-id="element-1080" />;
  }
  // Show results if completed
  if (results && !fetching) {
    return <div className="border border-primary/30 rounded-lg bg-card" data-id="element-1081">
        <div className="p-4 border-b border-border/30" data-id="element-1082">
          <div className="flex items-center justify-between mb-3" data-id="element-1083">
            <div className="flex items-center gap-2" data-id="element-1084">
              <MessageSquare size={16} className="text-primary" data-id="element-1085" />
              <h4 className="font-medium text-sm" data-id="element-1086">Comment Analysis Results</h4>
            </div>
            <div className="flex items-center gap-2" data-id="element-1087">
              <Button variant="ghost" size="sm" onClick={handleExportCSV} disabled={isExporting} className="h-8 px-2" data-id="element-1088">
                <FileSpreadsheet size={14} className="mr-1" data-id="element-1089" />
                CSV
              </Button>
              <Button variant="ghost" size="sm" onClick={handleExportJSON} disabled={isExporting} className="h-8 px-2" data-id="element-1090">
                <FileJson size={14} className="mr-1" data-id="element-1091" />
                JSON
              </Button>
            </div>
          </div>

          {exportError && <div className="mb-3 p-2 bg-destructive/10 border border-destructive/20 rounded text-xs text-destructive flex items-center gap-2" data-id="element-1092">
              <AlertCircle size={14} data-id="element-1093" />
              {exportError}
            </div>}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-id="element-1094">
            <div className="bg-secondary/50 rounded-lg p-3" data-id="element-1095">
              <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1" data-id="element-1096">
                <MessageSquare size={12} data-id="element-1097" />
                Total Comments
              </div>
              <div className="text-lg font-semibold" data-id="element-1098">
                {results.total_comments}
              </div>
            </div>
            <div className="bg-secondary/50 rounded-lg p-3" data-id="element-1099">
              <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1" data-id="element-1100">
                <Heart size={12} data-id="element-1101" />
                Avg Likes
              </div>
              <div className="text-lg font-semibold" data-id="element-1102">
                {Math.round(results.avg_likes).toLocaleString()}
              </div>
            </div>
            <div className="bg-secondary/50 rounded-lg p-3" data-id="element-1103">
              <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1" data-id="element-1104">
                <TrendingUp size={12} data-id="element-1105" />
                Engagement
              </div>
              <div className="text-lg font-semibold" data-id="element-1106">
                {results.engagement_rate.toFixed(1)}%
              </div>
            </div>
            <div className="bg-secondary/50 rounded-lg p-3" data-id="element-1107">
              <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1" data-id="element-1108">
                <BarChart3 size={12} data-id="element-1109" />
                Sentiment
              </div>
              <div className="text-sm font-semibold capitalize" data-id="element-1110">
                {results.sentiment.dominant} ({results.sentiment.positive}%)
              </div>
            </div>
          </div>
        </div>

        <div className="p-4" data-id="element-1111">
          <h5 className="text-sm font-medium mb-3 flex items-center gap-2" data-id="element-1112">
            <TrendingUp size={14} className="text-primary" data-id="element-1113" />
            Top Comments
          </h5>

          <div className="overflow-x-auto" data-id="element-1114">
            <table className="w-full text-sm" data-id="element-1115">
              <thead className="border-b border-border/30" data-id="element-1116">
                <tr className="text-left" data-id="element-1117">
                  <th className="pb-2 pr-4 font-medium text-muted-foreground" data-id="element-1118">
                    Author
                  </th>
                  <th className="pb-2 pr-4 font-medium text-muted-foreground" data-id="element-1119">
                    Comment
                  </th>
                  <th className="pb-2 pr-4 font-medium text-muted-foreground text-right" data-id="element-1120">
                    Likes
                  </th>
                  <th className="pb-2 font-medium text-muted-foreground text-right" data-id="element-1121">
                    Replies
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-1122">
                {results.top_comments.map((comment, index) => <tr key={index} className="border-b border-border/20 last:border-0" data-id="element-1123">
                    <td className="py-3 pr-4 text-xs" data-id="element-1124">{comment.author}</td>
                    <td className="py-3 pr-4 text-xs max-w-xs truncate" data-id="element-1125">
                      {comment.comment || comment.text}
                    </td>
                    <td className="py-3 pr-4 text-xs text-right" data-id="element-1126">
                      {comment.likes.toLocaleString()}
                    </td>
                    <td className="py-3 text-xs text-right" data-id="element-1127">
                      {comment.replies}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          <div className="mt-4 pt-4 border-t border-border/20 flex items-center justify-between" data-id="element-1128">
            <p className="text-xs text-muted-foreground" data-id="element-1129">
              Showing {results.top_comments.length} of {results.total_comments}{' '}
              comments
            </p>
            <Button variant="outline" size="sm" onClick={() => window.location.reload()} data-id="element-1130">
              <RefreshCw size={14} className="mr-1" data-id="element-1131" />
              Refresh
            </Button>
          </div>
        </div>
      </div>;
  }
  // Show preview by default
  return <>
      <CommentsPreview preview={preview} loading={previewLoading} error={previewError} alreadyFetched={alreadyFetched} onFetchClick={handleFetchClick} data-id="element-1132" />

      <CreditWarningModal isOpen={showCreditModal} onClose={() => setShowCreditModal(false)} onConfirm={handleConfirmFetch} creditsNeeded={preview?.credits_needed || 0} estimatedTotal={preview?.estimated_total || 0} data-id="element-1133" />
    </>;
}